import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { boardService } from '../../../lib/api/boards';
import { useJournalStore } from '../../../stores/journalStore';
import { PageLoader, ButtonSpinner } from '../../../components/LogoLoader';
import { useAuthStore } from '../../../stores/authStore';

interface ShareInfo {
    id: string;
    journal: {
        id: string;
        name: string;
    };
    owner: {
        name: string;
    };
    email?: string;
    permission: 'read' | 'readwrite';
    isOwner: boolean;
    alreadyAccepted: boolean;
}

export const AcceptSharePage: React.FC = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const { token } = useParams<{ token: string }>();
    const { loadSharedJournals } = useJournalStore();
    const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAccepting, setIsAccepting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (token) {
            verifyToken();
        }
    }, [token]);

    const verifyToken = async () => {
        try {
            const info = await boardService.verifyShareToken(token!);
            
            // If not authenticated, redirect to signup with pre-filled email
            if (!useAuthStore.getState().isAuthenticated) {
                const params = new URLSearchParams();
                params.set('modal', 'register');
                params.set('shareToken', token!);
                if (info.email) {
                    params.set('email', info.email);
                }
                navigate(`/?${params.toString()}`, { replace: true });
                return;
            }

            setShareInfo(info);
        } catch (err: any) {
            setError(err.message || 'Invalid or expired share link');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!token) return;

        setIsAccepting(true);
        try {
            await boardService.acceptShare(token);
            setSuccess(true);
            // Reload shared journals
            await loadSharedJournals(true);
            // Redirect after a short delay
            setTimeout(() => {
                if (shareInfo?.journal?.id) {
                    navigate(`/boards?tab=money_journal&journalTab=shared&journalId=${shareInfo.journal.id}`);
                } else {
                    navigate('/boards?tab=money_journal&journalTab=shared');
                }
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to accept share');
        } finally {
            setIsAccepting(false);
        }
    };

    const handleDecline = () => {
        navigate('/boards?tab=money_journal');
    };

    if (isLoading) {
        return <PageLoader />;
    }

    return (
        <div className="min-h-screen bg-background-page flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
                {error ? (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="w-8 h-8 text-red-500" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_01daf2ac.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_01daf2ac.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                        </div>
                        <h2 className="text-xl font-bold text-neutral-700 mb-2">
                            {t('sharing.invalidLink', 'Invalid Share Link')}
                        </h2>
                        <p className="text-neutral-500 mb-6">{error}</p>
                        <button
                            onClick={() => navigate('/boards?tab=money_journal')}
                            className="px-6 py-3 bg-neutral-100 text-neutral-700 font-medium rounded-xl hover:bg-neutral-200 transition-colors"
                        >
                            {t('common.goBack', 'Go Back')}
                        </button>
                    </div>
                ) : success ? (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="w-8 h-8 text-green-500" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_d699457c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d699457c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                        </div>
                        <h2 className="text-xl font-bold text-neutral-700 mb-2">
                            {t('sharing.accepted', 'Share Accepted!')}
                        </h2>
                        <p className="text-neutral-500 mb-2">
                            {t('sharing.acceptedDesc', 'The journal has been added to your shared journals.')}
                        </p>
                        <p className="text-sm text-neutral-400">
                            {t('sharing.redirecting', 'Redirecting...')}
                        </p>
                    </div>
                ) : shareInfo?.isOwner ? (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="w-8 h-8 text-amber-500" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_e39b0d1c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_e39b0d1c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                        </div>
                        <h2 className="text-xl font-bold text-neutral-700 mb-2">
                            {t('sharing.ownJournal', "This is Your Journal")}
                        </h2>
                        <p className="text-neutral-500 mb-6">
                            {t('sharing.ownJournalDesc', "You can't accept a share for your own journal.")}
                        </p>
                        <button
                            onClick={() => navigate(`/boards?tab=money_journal&journalTab=my&journalId=${shareInfo?.journal?.id}`)}
                            className="px-6 py-3 bg-accent-primary text-white font-medium rounded-xl hover:bg-accent-primary/90 transition-colors"
                        >
                            {t('sharing.goToJournals', 'Go to Journals')}
                        </button>
                    </div>
                ) : shareInfo?.alreadyAccepted ? (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="w-8 h-8 text-blue-500" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_22529c15.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_22529c15.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                        </div>
                        <h2 className="text-xl font-bold text-neutral-700 mb-2">
                            {t('sharing.alreadyAccepted', 'Already Accepted')}
                        </h2>
                        <p className="text-neutral-500 mb-6">
                            {t('sharing.alreadyAcceptedDesc', 'You already have access to this journal.')}
                        </p>
                        <button
                            onClick={() => navigate(`/boards?tab=money_journal&journalTab=shared&journalId=${shareInfo?.journal?.id}`)}
                            className="px-6 py-3 bg-accent-primary text-white font-medium rounded-xl hover:bg-accent-primary/90 transition-colors"
                        >
                            {t('sharing.goToJournals', 'Go to Journals')}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-6 border-b border-neutral-100 text-center">
                            <div className="w-16 h-16 bg-accent-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="w-8 h-8 text-accent-primary" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_4dfd2e34.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4dfd2e34.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                            </div>
                            <h2 className="text-xl font-bold text-neutral-700 mb-1">
                                {t('sharing.invitationTitle', 'Journal Shared With You')}
                            </h2>
                            <p className="text-neutral-500">
                                <span className="font-medium text-neutral-700">{shareInfo?.owner.name}</span>
                                {' '}{t('sharing.sharedWith', 'has shared a journal with you')}
                            </p>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Journal Info */}
                            <div className="bg-neutral-50 rounded-xl p-4 mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-primary/70 rounded-lg flex items-center justify-center text-white">
                                        <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_73496f11.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_73496f11.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-neutral-700">
                                            {shareInfo?.journal.name}
                                        </h3>
                                        <p className="text-sm text-neutral-500">
                                            {shareInfo?.permission === 'readwrite'
                                                ? t('sharing.canEdit', 'You can view and add operations')
                                                : t('sharing.canView', 'You can view operations')
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Permission Badge */}
                            <div className="flex justify-center mb-6">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${shareInfo?.permission === 'readwrite'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-blue-100 text-blue-700'
                                    }`}>
                                    {shareInfo?.permission === 'readwrite' ? (
                                        <>
                                            <span className="w-3.5 h-3.5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_f854d5d1.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f854d5d1.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            {t('sharing.readWriteAccess', 'Read & Write Access')}
                                        </>
                                    ) : (
                                        <>
                                            <span className="w-3.5 h-3.5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_51271fec.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_51271fec.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            {t('sharing.readOnlyAccess', 'Read Only Access')}
                                        </>
                                    )}
                                </span>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={handleDecline}
                                    className="flex-1 py-3 bg-neutral-100 text-neutral-700 font-medium rounded-xl hover:bg-neutral-200 transition-colors"
                                >
                                    {t('common.decline', 'Decline')}
                                </button>
                                <button
                                    onClick={handleAccept}
                                    disabled={isAccepting}
                                    className="flex-1 py-3 bg-accent-primary text-white font-medium rounded-xl hover:bg-accent-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isAccepting ? (
                                        <>
                                            <ButtonSpinner />
                                            {t('common.accepting', 'Accepting...')}
                                        </>
                                    ) : (
                                        t('common.accept', 'Accept')
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    );
};
